import React, {createRef, forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import PropTypes from 'prop-types';
import SVG from "react-inlinesvg";
import {toAbsoluteUrl} from "../../_metronic/_helpers";
import axios from "axios";


/**
 * Generate Column structure from data if not passed
 * @param {*} data table data
 */
const getCol = data => {
    let firstObj = data.length > 0 ? data[0] : {};
    let columns = []
    for (let key in firstObj) {
        if (firstObj.hasOwnProperty(key) && typeof (firstObj[key]) != "object") {
            columns.push({key, title: key.replace(/([A-Z])/g, " $1").replace(/_/g, " ").toUpperCase()})
        }
    }
    return columns
}

const DynamicSimpleTable = forwardRef(({source, queryParams, columns, searchBox, refreshIcon, onError, isSequential}, ref) => {

    /* Set React Component State */
    const [table, setTable] = useState({
        init: false,
        pg: 1,
        pgLoading: false,
        query: "",
        searchLoading: false,
        next: null,
        current: null,
        previous: null,
        data: null,
        rowPerPage: 10,
        searchTotal: 0,
        total: 0,
        ...source

    });

    /* Refresh table when queryParams is updated */
    const queryParamsRef = useRef(queryParams)
    useEffect(() => {
        if (queryParamsRef.current !== queryParams) {
            refreshTable()
            queryParamsRef.current = queryParams
        }
    }, [queryParams])

    const tableRef = createRef();
    const tableCol = columns ?? getCol(table.data ?? []);

    let firstRun = useRef({status: true, pg: table.pg});

    /* Auto scroll to top on new page */
    useEffect(() => {
        if (firstRun.current.status) {
            firstRun.current.status = !firstRun.current.status;
        } else if (firstRun.current.pg !== table.pg) {
            tableRef.current.scrollIntoView({behavior: 'smooth'});
            firstRun.current.pg = table.pg
        }
    }, [table.pg, tableRef])


    /* Generate Columns Titles */
    let colTitle = [];
    tableCol.forEach(({title}, i) => {
        colTitle.push(<th key={`th-${i}`} className="border-top-0">{title}</th>)
    });


    let records = [];


    /* Generate Rows */
    let start = (table.pg - 1) * table.rowPerPage;
    let showing = start + (table.data?.length ?? 0);

    for (let i in table.data) {

        records.push((function () {
            let row = []
            tableCol.forEach(({key, format}, j) => {
                /* Table Columns */
                if (format && typeof (format) === "function") {
                    row.push(<td key={`td-${i}${j}`} style={{verticalAlign: "middle"}}>{format({
                        ...table.data[i],
                        _index: (start + i)
                    })}</td>);
                } else if (typeof (table.data[i][key]) != "object") {
                    row.push(<td key={`td-${i}${j}`} style={{verticalAlign: "middle"}}>{table.data[i][key]}</td>);
                }
            })

            return <tr key={`tr-${i}`}>{row}</tr>
        })())
    }

    /* Pagination */
    let items = [];
    let addSpaceBefore = true
    let addSpaceAfter = true
    let maxPage = table.searchTotal > 0 ? Math.ceil(table.searchTotal / table.rowPerPage) : 1;

    /* Prevent Showing non existing page */
    if (table.pg > maxPage) {
        setTable({...table, pg: maxPage});
    }

    /* Delay Search input Execution to reduce flooding backend with too many request when typing  */
    const timeout = useRef();
    const searchInput = e => {
        const query = e.currentTarget.value?.trim();
        clearTimeout(timeout.current);
        timeout.current = setTimeout(_ => isSequential ? fetchSequenceData(null, query) : fetchData(1, query), 1000);
    }


    const goToPage = pgNumb => e => {
        e.preventDefault();
        pgNumb !== table.pg && fetchData(pgNumb, table.query);
    }

    const stepInSquence = cursor => e => {
        e.preventDefault();
        cursor !== table.pg && fetchSequenceData(cursor, table.query);
    }

    const fetchSequenceData = (cursor, query) => {
        const url = `${table.url}?${queryParams ? `${queryParams}&` : ""}cursor=${cursor || 1}&query=${query}&_=${new Date().getTime()}`;
        const updateTable = {
            ...table,
            query,
            init: true,
            pgLoading: cursor !== table.current,
            searchLoading: query !== table.query
        };
        setTable({...updateTable});

        axios.get(url, {headers: table.headers})
            .then(res => {
                if (res.data && Array.isArray(res.data.data)) {
                    setTable({...updateTable, ...res.data, current: cursor, pgLoading: false, searchLoading: false});
                }else {
                    console.log("herrrrr");
                    return Promise.reject({error: "Invalid Response", res});
                }
            })
            .catch(err => {
                if (typeof (onError) === "function") {
                    onError(err);
                } else {
                    console.log(err);
                }
            });
    }

    /* Fetch data from backend */
    const fetchData = (targetPg, query) => {

        const url = `${table.url}?${queryParams ? `${queryParams}&` : ""}pg=${targetPg}&query=${query}&_=${new Date().getTime()}`;
        const updateTable = {
            ...table,
            query,
            init: true,
            pgLoading: targetPg !== table.pg,
            searchLoading: query !== table.query
        };
        setTable({...updateTable});

        axios.get(url, {headers: table.headers})
            .then(res => {
                if (res.data && res.data.pg && Array.isArray(res.data.data) && Number.isInteger(res.data.searchTotal) && Number.isInteger(res.data.total)) {
                    setTable({...updateTable, ...res.data, pgLoading: false, searchLoading: false});
                }else {
                    return Promise.reject({error: "Invalid Response", res});
                }
            })
            .catch(err => {
                if (typeof (onError) === "function") {
                    onError(err);
                } else {
                    console.log(err);
                }
            });
    }


    /* Fetch Data on Load */
    useEffect(() => {
        if (!table.init) {
            isSequential ? fetchSequenceData(table.current, table.query) : fetchData(table.pg, table.query);
        }
    });

    /* Expose Method For refreshing the table Data */
    useImperativeHandle(ref, () => ({
        refresh: () => {
            refreshTable();
        }
    }));

    /* Refresh Table */
    const refreshTable = _ => {
        isSequential ? fetchSequenceData(table.current, table.query) : fetchData(table.pg, table.query);
        setTable({...table, data: null});
    }

    /* pagination Links and spacing */
    if (isSequential) {
        table.previous && items.push(
            <li
                className="page-item"
                key="pg-0"
                style={{marginRight: "0.5rem"}}
            >
                <span
                    className="page-link"
                    style={{cursor: "pointer"}}
                    onClick={stepInSquence(table.previous)}
                >
                    Prev
                </span>
            </li>
        );
        table.next && items.push(
            <li
                className="page-item"
                key="pg-0"
                style={{marginRight: "0.5rem"}}
            >
                <span
                    className="page-link"
                    style={{cursor: "pointer"}}
                    onClick={stepInSquence(table.next)}
                >
                    Next
                </span>
            </li>
        );
    } else {
        items.push(
            <li
                className="page-item"
                key="pg-0"
                style={{marginRight: "0.5rem"}}
            >
                <span
                    className="page-link"
                    style={{cursor: "pointer"}}
                    onClick={goToPage(table.pg === 1 ? 1 : table.pg - 1)}
                >
                    &lt;
                </span>
            </li>
        );

        for (let j = 1; j <= maxPage; j++) {
            if (j === table.pg) {
                items.push(
                    <li
                        className="page-item active"
                        key={`pg-${j}`}
                        style={{marginRight: "0.5rem"}}
                    >
                        <span
                            className="page-link"
                        >
                            {j}
                        </span>
                    </li>
                );
            } else if (j === 1 || j === maxPage || (j >= table.pg - 3 && j <= table.pg + 3)) {
                items.push(
                    <li
                        className="page-item"
                        key={`pg-${j}`}
                        style={{marginRight: "0.5rem"}}
                    >
                        <span
                            className="page-link"
                            style={{cursor: "pointer"}}
                            onClick={goToPage(j)}
                        >
                            {j}
                        </span>
                    </li>
                );
            } else if (j < table.pg - 3 && addSpaceBefore) {
                items.push(
                    <li
                        className="page-item disabled"
                        key={`pg-${j}`}
                        style={{marginRight: "0.5rem"}}
                    >
                        <span
                            className="page-link disabled"
                        >
                            ...
                        </span>
                    </li>
                );
                addSpaceBefore = false;
            } else if (j > table.pg + 3 && addSpaceAfter) {
                items.push(
                    <li
                        className="page-item disabled"
                        key={`pg-${j}`}
                        style={{marginRight: "0.5rem"}}
                    >
                        <span
                            className="page-link disabled"
                        >
                            ...
                        </span>
                    </li>
                );
                addSpaceAfter = false;
            }
        }

        items.push(
            <li
                className="page-item"
                key={`pg-${maxPage + 1}`}
                style={{marginRight: "0.5rem"}}
            >
                <span
                    className="page-link"
                    style={{cursor: "pointer"}}
                    onClick={goToPage(maxPage > table.pg ? table.pg + 1 : table.pg)}
                >
                    &gt;
                </span>
            </li>
        );
    }
    

    

    return <div className="my-simple-react-table">
        <div className="table-responsive py-5" ref={tableRef} style={{minHeight: "20rem"}}>
            {
                refreshIcon &&
                <button className="btn" onClick={refreshTable}>
                    <span className="svg-icon svg-icon-lg svg-icon-primary">
                        <SVG
                            title="Refresh Table"
                            src={toAbsoluteUrl("/media/svg/icons/Code/Time-schedule.svg")}
                        />
                    </span>
                </button>
            }

            {
                searchBox &&
                <div className="input-icon"
                     style={{maxWidth: "220px", float: "right", marginBottom: "0.5rem"}}
                >
                    <input
                        type="search"
                        placeholder="Search"
                        className="form-control"
                        onInput={searchInput}
                    />
                    <span>
                        {
                            table.searchLoading ?
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                                :
                                <span className="svg-icon svg-icon-lg">
                                    <SVG
                                        src={toAbsoluteUrl("/media/svg/icons/General/Search.svg")}
                                    />
                                </span>
                        }
                    </span>
                </div>
            }
            <table className="table">
                <thead className="text-muted">
                <tr>
                    {colTitle}
                </tr>
                </thead>
                <tbody>
    {records.length === 0 ? (
        <tr>
            <td className="text-center p-4" colSpan="9">
                {table.error === "Multiple users with this name. Try searching with a specific email or userId." ? (
                    <>
                        Multiple users with this name. Try searching with a specific email or userId.
                        <br />
                        No {table.query === "" ? "Record" : "Match"} Found
                    </>
                ) : table.data ? (
                    <>No {table.query === "" ? "Record" : "Match"} Found</>
                ) : (
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                )}
            </td>
        </tr>
    ) : (
        records
    )}
</tbody>

            </table>
        </div>

        <div className="px-3 row">
            <div className="col-sm">
                <ul className="pagination pagination-sm">{items}
                    {
                        table.pgLoading && <li>
                            <div className="spinner-border spinner-border-sm text-primary ml-3" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </li>
                    }
                </ul>
            </div>
            {
                !isSequential && <div className="text-right col-sm">
                    Showing {showing} of {table.searchTotal} {table.data && table.total > table.searchTotal ? `(filtered from ${table.total} total)` : ""}
                </div>
            }
        </div>
    </div>

})


DynamicSimpleTable.defaultProps = {
    searchBox: true,
    refreshIcon: false
};

DynamicSimpleTable.propTypes = {
    source: PropTypes.object,
    columns: PropTypes.array,
    searchBox: PropTypes.bool
}

export default DynamicSimpleTable;